import React, {useCallback, useState} from 'react';
import {Box, Button, IconButton, Modal, Typography} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import styles, {ORANGE_COLOR} from '../../styles';
import {useTranslation} from 'react-i18next';
import {Control, FieldValues, useForm} from 'react-hook-form';
import FormCheckbox from './FormCheckbox';
import {PositionGroup, Positions} from '../../enums';

export interface UtilizationFilterForm {
  [PositionGroup.forwards]: boolean;
  [Positions.center]: boolean;
  [Positions.leftwing]: boolean;
  [Positions.rightwing]: boolean;
  [Positions.defense]: boolean;
}

interface Props {
  currentFilters?: UtilizationFilterForm;
  onFilterUpdate: (newFilters: UtilizationFilterForm) => void;
}

const defaultFilterValue: UtilizationFilterForm = {
  [PositionGroup.forwards]: false,
  [Positions.center]: false,
  [Positions.leftwing]: false,
  [Positions.rightwing]: false,
  [Positions.defense]: false,
};

const TeamShiftUtilizationFilter: React.FC<Props> = ({
  currentFilters,
  onFilterUpdate,
}) => {
  const {t} = useTranslation();
  const {handleSubmit, reset, control, setValue} =
    useForm<UtilizationFilterForm>({
      defaultValues: currentFilters || defaultFilterValue,
    });
  const [showModal, setShowModal] = useState(false);

  const onCancel = useCallback(() => {
    reset(currentFilters || defaultFilterValue);
    setShowModal(false);
  }, [currentFilters, reset]);

  const onApply = useCallback(
    (data: UtilizationFilterForm) => {
      onFilterUpdate(data);
      setShowModal(false);
    },
    [onFilterUpdate],
  );

  return (
    <>
      <IconButton
        sx={
          currentFilters && Object.values(currentFilters).some(val => val)
            ? {
                color: ORANGE_COLOR,
              }
            : {}
        }
        onClick={() => setShowModal(true)}>
        <FilterAltIcon />
      </IconButton>
      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
        }}>
        <form onSubmit={handleSubmit(onApply)} className="filterform">
          <Box sx={styles.modal}>
            <Typography variant="h3" sx={{mb: 2}}>
              {t('teamShiftUtilization.filters')}
            </Typography>
            <Box sx={{padding: 2}}>
              <Typography variant="h4" sx={{mb: 2}}>
                {t('position.label')}
              </Typography>
              <FormCheckbox
                control={control as unknown as Control<FieldValues>}
                name={PositionGroup.forwards}
                label={`${t('position.forwards')}`}
                handleAdditionalOnChange={e => {
                  const isChecked = e.target.checked;
                  // programmatically check the center, leftwing, and rightwing checkboxes
                  if (isChecked) {
                    setValue(Positions.center, true);
                    setValue(Positions.leftwing, true);
                    setValue(Positions.rightwing, true);
                  } else {
                    setValue(Positions.center, false);
                    setValue(Positions.leftwing, false);
                    setValue(Positions.rightwing, false);
                  }
                }}
              />
              <Box sx={{ml: 3}}>
                <FormCheckbox
                  control={control as unknown as Control<FieldValues>}
                  name={Positions.center}
                  label={`${t('position.center')}`}
                  handleAdditionalOnChange={e => {
                    const isChecked = e.target.checked;
                    if (!isChecked) {
                      setValue(PositionGroup.forwards, false);
                    }
                  }}
                />
                <FormCheckbox
                  control={control as unknown as Control<FieldValues>}
                  name={Positions.leftwing}
                  label={`${t('position.leftwing')}`}
                  handleAdditionalOnChange={e => {
                    const isChecked = e.target.checked;
                    if (!isChecked) {
                      setValue(Positions.center, false);
                    }
                  }}
                />
                <FormCheckbox
                  control={control as unknown as Control<FieldValues>}
                  name={Positions.rightwing}
                  label={`${t('position.rightwing')}`}
                  handleAdditionalOnChange={e => {
                    const isChecked = e.target.checked;
                    if (!isChecked) {
                      setValue(Positions.center, false);
                    }
                  }}
                />
              </Box>
              <FormCheckbox
                control={control as unknown as Control<FieldValues>}
                name={Positions.defense}
                label={`${t('position.defense')}`}
              />
            </Box>
            <Box sx={{textAlign: 'right'}}>
              <Button sx={{mr: 2}} onClick={onCancel}>
                {t('general.cancel')}
              </Button>
              <Button variant="contained" type="submit">
                {t('general.apply')}
              </Button>
            </Box>
          </Box>
        </form>
      </Modal>
    </>
  );
};

export default TeamShiftUtilizationFilter;
